import { Building2, Check, ChevronsUpDown, LayoutGrid, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router";

import type { SessionMembership } from "@/api/types";
import { KMark } from "@/components/brand/KMark";
import { Avatar, Menu, MenuItem, MenuLabel, MenuSeparator } from "@/components/ui";
import { cx } from "@/lib/cx";

type Props = {
  compact: boolean;
  name: string;
  logoUrl?: string | null;
  currentSlug?: string;
  memberships: SessionMembership[];
  platform: boolean;
  impersonating: boolean;
  subtitle?: string;
};

/** Current workspace identity and switcher. Only lists workspaces with internal access. */
export function WorkspaceSwitcher({ compact, name, logoUrl, currentSlug, memberships, platform, impersonating, subtitle }: Props) {
  const navigate = useNavigate();
  const workspaces = memberships.filter((m) => m.has_internal_access);
  const canSwitch = !impersonating && (workspaces.length > 1 || platform || (currentSlug === undefined && workspaces.length > 0));
  const identity = logoUrl ? (
    <Avatar name={name} src={logoUrl} size={compact ? "md" : "sm"} square />
  ) : currentSlug ? (
    <Avatar name={name} size={compact ? "md" : "sm"} square className="bg-accent-subtle text-accent-text" />
  ) : (
    <span className={cx("flex items-center justify-center rounded-md bg-[#0B0C0E] text-white", compact ? "size-8" : "size-6")}>
      <KMark className={compact ? "size-4" : "size-3"} />
    </span>
  );

  const trigger = compact ? (
    <button
      type="button"
      data-tour="workspace-switcher"
      aria-label={canSwitch ? `Workspace: ${name}. Switch workspace` : `Workspace: ${name}`}
      disabled={!canSwitch}
      className="flex size-10 items-center justify-center rounded-md hover:bg-surface-hover disabled:cursor-default disabled:hover:bg-transparent"
    >
      {identity}
    </button>
  ) : (
    <button
      type="button"
      disabled={!canSwitch}
      className="flex w-full flex-col gap-2 rounded-md px-1 py-1 text-left hover:bg-surface-hover disabled:cursor-default disabled:hover:bg-transparent"
    >
      {/* A tenant logo gets the full panel width (capped in height) above the name, on a light tile. */}
      {logoUrl && <img src={logoUrl} alt="" className="h-auto max-h-16 w-full rounded-md border border-line bg-logo-tile object-contain p-1.5" />}
      <span className="flex w-full items-center gap-2">
        {!logoUrl && identity}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-fg">{name}</span>
          {subtitle && <span className="block truncate text-xs text-fg-subtle">{subtitle}</span>}
        </span>
        {canSwitch && <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-fg-subtle" />}
      </span>
    </button>
  );

  if (!canSwitch) return trigger;
  return (
    <Menu trigger={trigger} side={compact ? "right" : "bottom"} align="start" label="Switch workspace">
      <MenuLabel>Workspaces</MenuLabel>
      {workspaces.map((m) => (
        <MenuItem
          key={m.id}
          icon={m.tenant.slug === currentSlug ? <Check /> : <Building2 />}
          onSelect={() => navigate(`/t/${m.tenant.slug}`)}
        >
          {m.tenant.name}
        </MenuItem>
      ))}
      {platform && (
        <>
          <MenuSeparator />
          <MenuItem icon={<ShieldCheck />} onSelect={() => navigate("/platform/tenants")}>
            Kasten platform
          </MenuItem>
        </>
      )}
      <MenuSeparator />
      <MenuItem icon={<LayoutGrid />} onSelect={() => navigate("/workspaces")}>
        All workspaces
      </MenuItem>
    </Menu>
  );
}
